import { ImageSliderProps, AdaptedImageSliderProps, ImageSliderEditorProps, ImageItem } from './ImageSlider.types';

/**
 * 기존 데이터 형식을 새로운 형식으로 변환
 * 
 * @param originalProps 기존 컴포넌트의 props
 * @returns 변환된 새로운 컴포넌트용 props
 */
export function adaptImageSliderProps(originalProps: any): AdaptedImageSliderProps {
  // 기본값 설정 (빈 객체로 초기화)
  const originalContent = originalProps?.content || {};
  const originalData = originalProps?.data || {};
  const originalComponentProps = originalProps?.componentProps || {};
  
  // 스타일 병합 (깊은 복사로 병합)
  const style = {
    ...(originalProps?.style || {}),
    ...(originalComponentProps?.style || {})
  };
  
  // 인라인 스타일과 CSS 클래스 통합
  if (originalProps.defaultStyle) {
    Object.assign(style, originalProps.defaultStyle);
  }

  // className 설정
  const className = originalProps?.className || originalComponentProps?.className || '';

  // 슬라이더 제목
  const sliderTitle = originalComponentProps?.sliderTitle || 
                      originalContent?.sliderTitle || 
                      '이미지 슬라이더';

  // 슬라이더 제목 표시 여부
  const showTitle = originalComponentProps?.showTitle !== undefined
    ? originalComponentProps.showTitle
    : originalContent?.showTitle !== undefined
      ? originalContent.showTitle
      : false;

  // 제목 스타일 속성
  const titleFontSize = originalComponentProps?.titleFontSize ||
                        originalContent?.titleFontSize ||
                        '24px';
  
  const titleFontWeight = originalComponentProps?.titleFontWeight ||
                          originalContent?.titleFontWeight ||
                          '600';
  
  const titleColor = originalComponentProps?.titleColor ||
                     originalContent?.titleColor ||
                     '#333';

  // 슬라이더 높이
  const sliderHeight = originalComponentProps?.sliderHeight ||
                       originalContent?.sliderHeight ||
                       '400px';

  // 한 화면에 보여줄 이미지 개수
  const slidesToShow = Number(originalComponentProps?.slidesToShow ||
                             originalContent?.slidesToShow ||
                             1);

  // 슬라이드 속성
  const autoSlide = originalComponentProps?.autoSlide !== undefined
    ? originalComponentProps.autoSlide
    : originalContent?.autoSlide !== undefined
      ? originalContent.autoSlide
      : true;
  
  const autoSlideInterval = Number(originalComponentProps?.autoSlideInterval ||
                                  originalContent?.autoSlideInterval ||
                                  4000);

  // 무한 슬라이드 여부
  const infiniteSlide = originalComponentProps?.infiniteSlide !== undefined
    ? originalComponentProps.infiniteSlide
    : originalContent?.infiniteSlide !== undefined
      ? originalContent.infiniteSlide
      : true;

  // 네비게이션 표시 여부
  const showNavigation = originalComponentProps?.showNavigation !== undefined
    ? originalComponentProps.showNavigation
    : originalContent?.showNavigation !== undefined
      ? originalContent.showNavigation
      : true;

  // 인디케이터 표시 여부
  const showIndicators = originalComponentProps?.showIndicators !== undefined
    ? originalComponentProps.showIndicators
    : originalContent?.showIndicators !== undefined
      ? originalContent.showIndicators
      : true;

  // 이미지 객체 맞춤 설정
  const imageObjectFit = originalComponentProps?.imageObjectFit ||
                         originalContent?.imageObjectFit ||
                         'cover';

  // 이미지 목록 처리
  const images: ImageItem[] = originalComponentProps?.images ||
                              originalContent?.images ||
                              originalProps?.images ||
                              [
                                {
                                  id: '1',
                                  url: 'https://via.placeholder.com/800x400/cccccc/666666?text=Sample+Image+1',
                                  title: '샘플 이미지 1',
                                  alt: '샘플 이미지 1',
                                  link: '',
                                  openInNewTab: false
                                },
                                {
                                  id: '2',
                                  url: 'https://via.placeholder.com/800x400/dddddd/777777?text=Sample+Image+2',
                                  title: '샘플 이미지 2',
                                  alt: '샘플 이미지 2',
                                  link: '',
                                  openInNewTab: false
                                }
                              ];

  // 변환된 props 반환
  const adaptedProps: AdaptedImageSliderProps = {
    id: originalProps?.id || '',
    style,
    className,
    data: {
      sliderTitle,
      showTitle,
      titleFontSize,
      titleFontWeight,
      titleColor,
      sliderHeight,
      slidesToShow,
      autoSlide,
      autoSlideInterval,
      infiniteSlide,
      showNavigation,
      showIndicators,
      imageObjectFit,
      images,
      device: originalProps?.device || originalComponentProps?.device || {
        pc: {},
        mobile: {}
      }
    }
  };

  return adaptedProps;
}

/**
 * 에디터용 데이터 형식 변환
 * 
 * @param originalProps 기존 컴포넌트의 props
 * @returns 변환된 에디터 컴포넌트용 props
 */
export function adaptImageSliderEditorProps(originalProps: any): ImageSliderEditorProps {
  // 기본 어댑터를 통해 변환된 데이터를 원래 ImageSliderProps 형태로 변환
  const adaptedProps = adaptImageSliderProps(originalProps);
  
  // AdaptedImageSliderProps를 ImageSliderProps로 변환
  const imageSliderProps: ImageSliderProps = {
    id: adaptedProps.id,
    style: adaptedProps.style,
    className: adaptedProps.className,
    componentProps: {
      sliderTitle: adaptedProps.data?.sliderTitle,
      showTitle: adaptedProps.data?.showTitle,
      titleFontSize: adaptedProps.data?.titleFontSize,
      titleFontWeight: adaptedProps.data?.titleFontWeight,
      titleColor: adaptedProps.data?.titleColor,
      sliderHeight: adaptedProps.data?.sliderHeight,
      slidesToShow: adaptedProps.data?.slidesToShow,
      autoSlide: adaptedProps.data?.autoSlide,
      autoSlideInterval: adaptedProps.data?.autoSlideInterval,
      infiniteSlide: adaptedProps.data?.infiniteSlide,
      showNavigation: adaptedProps.data?.showNavigation,
      showIndicators: adaptedProps.data?.showIndicators,
      imageObjectFit: adaptedProps.data?.imageObjectFit,
      images: adaptedProps.data?.images
    },
    images: adaptedProps.data?.images
  };
  
  // 에디터 관련 추가 props
  return {
    ...imageSliderProps,
    isSelected: originalProps.isSelected || false,
    isPreview: originalProps.isPreview || false,
    viewMode: originalProps.viewMode || 'pc',
    updateComponent: originalProps.updateComponent || (() => {})
  };
} 